// 세금계산서 다운로드/공유 액션 컴포넌트

import { Button } from '../ui/button';
import { Download, Mail, MessageCircle, FileText, Copy, ExternalLink, Loader2, Check } from 'lucide-react';
import { useLanguage } from '@/contexts/AppContext';
import type { InvoiceTranslations } from './types';

interface InvoiceActionsProps {
  translations: InvoiceTranslations;
  // PDF 다운로드 관련
  pdfDownloading: boolean;
  pdfSuccess: boolean;
  onDownloadPDF: () => void;
  // XML 다운로드 관련
  xmlDownloading: boolean;
  xmlSuccess: boolean;
  onDownloadXML: () => void;
  // 공유 관련
  onEmailClick: () => void;
  onWhatsAppShare: () => void;
  onCopyLink: () => void;
  onViewOnline: () => void;
}

export function InvoiceActions({
  translations,
  pdfDownloading,
  pdfSuccess,
  onDownloadPDF,
  xmlDownloading,
  xmlSuccess,
  onDownloadXML,
  onEmailClick,
  onWhatsAppShare,
  onCopyLink,
  onViewOnline
}: InvoiceActionsProps) {
  const { language } = useLanguage();
  const getDownloadingText = () => {
    if (language === 'ko') return '다운로드 중...';
    if (language === 'es') return 'Descargando...';
    return 'Downloading...';
  };

  const getCompleteText = () => {
    if (language === 'ko') return '완료!';
    if (language === 'es') return '!Completado!';
    return 'Complete!';
  };

  return (
    <div>
      <h3 className="font-medium text-foreground mb-4">
        {translations.downloadOptions}
      </h3>

      {/* 다운로드 버튼 */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Button
          onClick={onDownloadPDF}
          variant="outline"
          disabled={pdfDownloading}
          className="relative flex flex-col items-center gap-2 h-auto py-4 overflow-hidden transition-all duration-300 hover:shadow-md hover:scale-105 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100 group"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          <div className="relative z-10 transition-transform duration-300 group-hover:scale-110">
            {pdfDownloading ? (
              <Loader2 className="w-5 h-5 text-red-600 animate-spin" />
            ) : pdfSuccess ? (
              <Check className="w-5 h-5 text-green-600 animate-bounce" />
            ) : (
              <Download className="w-5 h-5 text-red-600 group-hover:animate-pulse" />
            )}
          </div>

          <span className="relative z-10 text-sm font-medium transition-colors duration-300">
            {pdfDownloading ? getDownloadingText() : pdfSuccess ? getCompleteText() : translations.downloadPDF}
          </span>

          {pdfSuccess && <div className="absolute inset-0 bg-green-50 animate-pulse" />}
        </Button>

        <Button
          onClick={onDownloadXML}
          variant="outline"
          disabled={xmlDownloading}
          className="relative flex flex-col items-center gap-2 h-auto py-4 overflow-hidden transition-all duration-300 hover:shadow-md hover:scale-105 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100 group"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          <div className="relative z-10 transition-transform duration-300 group-hover:scale-110">
            {xmlDownloading ? (
              <Loader2 className="w-5 h-5 text-green-600 animate-spin" />
            ) : xmlSuccess ? (
              <Check className="w-5 h-5 text-green-600 animate-bounce" />
            ) : (
              <FileText className="w-5 h-5 text-green-600 group-hover:animate-pulse" />
            )}
          </div>

          <span className="relative z-10 text-sm font-medium transition-colors duration-300">
            {xmlDownloading ? getDownloadingText() : xmlSuccess ? getCompleteText() : translations.downloadXML}
          </span>

          {xmlSuccess && <div className="absolute inset-0 bg-green-50 animate-pulse" />}
        </Button>
      </div>

      {/* 공유 버튼 */}
      <div className="grid grid-cols-2 gap-3">
        <Button onClick={onEmailClick} variant="outline" className="flex items-center gap-2">
          <Mail className="w-4 h-4" />
          <span className="text-sm">{translations.emailSend}</span>
        </Button>
        <Button onClick={onWhatsAppShare} variant="outline" className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4" />
          <span className="text-sm">{translations.whatsappSend}</span>
        </Button>
      </div>

      {/* 추가 액션 */}
      <div className="grid grid-cols-2 gap-3 mt-3">
        <Button onClick={onCopyLink} variant="outline" className="flex items-center gap-2">
          <Copy className="w-4 h-4" />
          <span className="text-sm">{translations.copyLink}</span>
        </Button>
        <Button onClick={onViewOnline} variant="outline" className="flex items-center gap-2">
          <ExternalLink className="w-4 h-4" />
          <span className="text-sm">{translations.viewOnline}</span>
        </Button>
      </div>
    </div>
  );
}
